// Suivi en direct des jobs d'agents (SSE + repli polling), barre flottante.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "./api";

const STREAM_BASE = process.env.NODE_ENV === "development" ? "http://localhost:4000" : "";

const ProgressCtx = createContext(null);
export function useProgress() { return useContext(ProgressCtx); }

export function ProgressProvider({ children }) {
  const [jobs, setJobs] = useState([]);

  const start = useCallback((job) => {
    setJobs((prev) => prev.some((j) => j.jobId === job.jobId) ? prev
      : [...prev, { ...job, state: { pct: 0, msg: "Démarrage…", status: "running" } }]);
  }, []);
  const updateState = useCallback((jobId, state) =>
    setJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, state } : j))), []);
  const remove = useCallback((jobId) => setJobs((prev) => prev.filter((j) => j.jobId !== jobId)), []);
  const cancel = useCallback(async (jobId) => {
    try { await api(`/jobs/${jobId}/cancel`, { method: "POST" }); } catch { /* */ }
  }, []);

  const run = useCallback(async ({ path, method = "POST", body, label, onDone, onError }) => {
    let r;
    try { r = await api(path, { method, body }); }
    catch (e) { onError?.(e.message || "Erreur"); return; }
    if (r && r.jobId) start({ jobId: r.jobId, label, onDone, onError });
    else onDone?.(r);
  }, [start]);

  return (
    <ProgressCtx.Provider value={{ start, run, jobs, cancel, remove, updateState }}>
      {children}
    </ProgressCtx.Provider>
  );
}

function JobLine({ job }) {
  const { updateState, remove, cancel } = useProgress();
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;
    let es = null, pollTimer = null;

    const finish = async (data) => {
      if (closedRef.current) return;
      closedRef.current = true;
      if (es) try { es.close(); } catch { /* */ }
      if (pollTimer) clearInterval(pollTimer);
      updateState(job.jobId, data);
      if (data.status === "done") {
        let result = null;
        try { const full = await api(`/jobs/${job.jobId}`); result = full.result; } catch { /* */ }
        job.onDone?.(result);
      } else if (data.status === "error") job.onError?.(data.msg || "Échec");
      setTimeout(() => remove(job.jobId), 1800);
    };
    const apply = (data) => {
      updateState(job.jobId, data);
      if (data.status === "done" || data.status === "error") finish(data);
    };
    function startPolling() {
      if (pollTimer || closedRef.current) return;
      pollTimer = setInterval(async () => {
        if (closedRef.current) return;
        try {
          const j = await api(`/jobs/${job.jobId}`);
          apply({
            status: j.status === "done" ? "done" : (["error", "cancelled"].includes(j.status) ? "error" : "running"),
            msg: j.progressMsg, pct: j.progressPct,
          });
        } catch (e) { finish({ status: "error", msg: e.message }); }
      }, 900);
    }

    // Ticket éphémère à usage unique (05/09) : le JWT de session ne transite plus en query.
    api(`/jobs/${job.jobId}/ticket`, { method: "POST" }).then(({ ticket }) => {
      if (closedRef.current) return;
      try {
        es = new EventSource(`${STREAM_BASE}/api/jobs/${job.jobId}/stream?ticket=${ticket}`);
        es.onmessage = (e) => { try { apply(JSON.parse(e.data)); } catch { /* */ } };
        es.onerror = () => { if (es) { try { es.close(); } catch { /* */ } es = null; } startPolling(); };
      } catch { startPolling(); }
    }).catch(() => startPolling());

    return () => {
      closedRef.current = true;
      if (es) try { es.close(); } catch { /* */ }
      if (pollTimer) clearInterval(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.jobId]);

  const state = job.state;
  const pct = Math.max(0, Math.min(100, parseInt(state.pct, 10) || 0));
  const isErr = state.status === "error";
  const isDone = state.status === "done";
  return (
    <div className="job-line">
      <div className="job-line-top">
        <span className={"pulse-dot" + (isErr ? " error" : "") + (isDone ? " done" : "")} />
        <span className="progress-title">{job.label}</span>
        <span className="progress-pct">{pct}%</span>
        <button className="job-cancel" title="Annuler" disabled={isDone || isErr}
          onClick={() => cancel(job.jobId)}>✕</button>
      </div>
      <div className="progress-bar"><div className={"progress-fill" + (isErr ? " error" : "")} style={{ width: pct + "%" }} /></div>
      <div className={"progress-step" + (isErr ? " error" : "")}>{state.msg || "…"}</div>
    </div>
  );
}

export function ProgressStrip() {
  const ctx = useProgress();
  if (!ctx?.jobs?.length) return null;
  return (
    <div className="progress-strip">
      {ctx.jobs.length > 1 && <div className="progress-strip-header">{ctx.jobs.length} agents en cours</div>}
      {ctx.jobs.map((j) => <JobLine key={j.jobId} job={j} />)}
    </div>
  );
}
